import React, { JSX, useMemo, useRef } from 'react';
import { Flex, InputRef } from 'antd';
import classNames from 'classnames';
import { DeleteIcon, PencilIcon } from '../../Icons';
import { CommentItem } from '../types';
import { CommentInput } from '../CommentInput';

import cn from './styles.module.scss';

type Props = {
  comment: CommentItem;
  isLoading: boolean;
  onDelete: (id: number) => void;
  onUpdate: (id: number, commentText: string) => void;
  isEditAvailable?: boolean;
  isDeleteAvailable?: boolean;
};

export const Comment = ({
  isLoading, comment, onDelete, onUpdate, isEditAvailable, isDeleteAvailable,
}: Props): JSX.Element => {
  const ref = useRef<InputRef | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  const isModifyButtonsVisible = isEditAvailable || isDeleteAvailable;
  const isHeaderVisible = comment.createdAt || comment.user?.fullName;

  const handleEditingComment = () => {
    if (isDeleting || isUpdating) {
      return;
    }
    setIsEditing(true);
  };

  const handleUpdateComment = async (text: string) => {
    if (isDeleting || isUpdating) {
      return;
    }
    try {
      setIsUpdating(true);
      setIsEditing(false);
      await onUpdate(comment.id, text);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (isDeleting || isUpdating) {
      return;
    }
    try {
      setIsDeleting(true);
      await onDelete(id);
    } finally {
      setIsDeleting(false);
    }
  };

  const formattedCreatedAt = useMemo(() => {
    if (!comment.createdAt) {
      return '';
    }
    const formatter = new Intl.DateTimeFormat(
      'en-US',
      { day: '2-digit', month: 'short', year: 'numeric' },
    );
    const dateCreatedAt = new Date(comment.createdAt);
    return formatter.format(dateCreatedAt).replace(/,/, '');
  }, [comment.createdAt]);

  return (
    <Flex
      key={comment.id}
      vertical
      gap={6}
      className={classNames(cn.comment, { [cn.hovered]: isHovered && (isEditAvailable || isDeleteAvailable) })}
      onMouseOver={() => setIsHovered(true)}
      onFocus={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid="commentContainer"
    >
      {isHeaderVisible && (
        <Flex justify="space-between" className={cn.commentHeader}>
          <Flex className={cn.commentAuthor}>
            {comment.user?.fullName}
          </Flex>

          <Flex className={cn.commentActions}>
            <Flex className={cn.commentDate}>
              {formattedCreatedAt}
            </Flex>
            {isModifyButtonsVisible && (
              <Flex className={cn.commentActionButtons}>
                {isEditAvailable && (
                  <PencilIcon
                    className={cn.commentActionButton}
                    onClick={handleEditingComment}
                    data-testid="commentEditIcon"
                  />
                )}
                {isDeleteAvailable && (
                  <DeleteIcon
                    className={cn.commentActionButton}
                    onClick={() => handleDelete(comment.id)}
                    data-testid="commentDeleteIcon"
                  />
                )}
              </Flex>
            )}
          </Flex>
        </Flex>
      )}

      <Flex className={cn.commentBody}>
        {!isEditing && (
          <pre>
            {comment.commentText}
          </pre>
        )}
        {isEditing && (
          <CommentInput
            className={cn.input}
            defaultValue={comment.commentText}
            isLoading={isLoading}
            onSubmit={handleUpdateComment}
            onBlur={handleUpdateComment}
            ref={ref}
            placeholder="Edit your comment"
          />
        )}
      </Flex>
    </Flex>
  );
};
